import { createCommand, HomeButton } from "@cesium/widgets";
import Utils from "./Utils";

/**
 * 현재 scene의 기본 카메라 뷰 또는 지정한 위치로 돌아가는 단일 버튼 위젯.</br>
 * A single button widget for returning to the default camera view of the current scene or to a specific location.
 * @extends HomeButton
 *
 * @alias DitapHomeButton
 * @constructor
 *
 * @param {Element|string} container 위젯을 포함할 DOM 요소 또는 ID.</br> The DOM element or ID that will contain the widget.
 * @param {Scene} scene 사용할 Scene 인스턴스.</br> The Scene instance to use.
 * @param {Cartesian3} [destination] 카메라 이동하려는 목적지. 입력하지 않은 경우 현재 scene의 기본 카메라 뷰로 이동. </br>
 * The destination to which the camera is intended to move. If not specified, it moves to the default camera view of the current scene.
 *
 * @example
 * // 1. 뷰어 선언 시 활성화
 * // 1-1. 기본 위치로 활성화 할 경우
 * const viewer = new Ditap.DitapViewer("ditapContainer", {
 *     homeButton: true
 * });
 * // 1-2. 초기 위치 지정(Cartesian3 값만 허용)
 * const viewer = new Ditap.DitapViewer("ditapContainer", {
 *     // 초기 위치 지정
 *     homeButton: Ditap.Cartesian3.fromDegrees(
 *        126.978275264,
 *        37.566642192,
 *        400
 *     ),
 * });
 *
 * // 2. 단독으로 선언하여 활성화
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 * const container = viewer.container;
 *
 * // 홈 버튼 생성 할 컨테이너
 * const homebuttonContainer = viewer.container.getElementsByClassName(
 *    "js-ditap-toolbal-top-wrap"
 * )[0];
 *
 * // 초기위치 초기화
 * const destination = Ditap.Cartesian3.fromDegrees(
 *    126.978275264,
 *    37.566642192,
 *    400
 * );
 *
 * new Ditap.DitapHomeButton(
 *    homebuttonContainer,
 *    viewer.scene,
 *    destination
 * );
 */
class DitapHomeButton extends HomeButton {
  constructor(viewer, destination) {
    const container = viewer.toolbarTopDiv;
    const mobileContainer = viewer.mobileBtnContainer;
    const scene = viewer.scene;

    super(container, scene);

    this._container = container;
    this.isMobile = Utils.isBodyWidthLessThan600();

    if (destination) {
      this.viewModel._command = createCommand(function () {
        scene.camera.flyTo({
          destination: destination,
        });
      });
    }

    const cesiumHomeButton = container.getElementsByClassName(
      "cesium-home-button"
    )[0];

    if (cesiumHomeButton) {
      cesiumHomeButton.firstElementChild.remove();
      if (cesiumHomeButton.classList.contains("cesium-button")) {
        cesiumHomeButton.classList.remove("cesium-button");
      }
      if (cesiumHomeButton.classList.contains("cesium-toolbar-button")) {
        cesiumHomeButton.classList.remove("cesium-toolbar-button");
      }
      if (cesiumHomeButton.classList.contains("cesium-home-button")) {
        cesiumHomeButton.classList.remove("cesium-home-button");
      }

      cesiumHomeButton.classList.add("ditap-home-btn");
      cesiumHomeButton.removeAttribute("title");

      const li = document.createElement("li");
      li.classList.add("ditap-func-btn-item");

      if (this.isMobile) {
        li.appendChild(cesiumHomeButton);
        // mobileContainer.prepend(li);
        mobileContainer.querySelector(".js-position-home").appendChild(li);
        cesiumHomeButton.classList.add("ditap-mobile-btn");
      } else {
        cesiumHomeButton.classList.add("ditap-toolbar-btn");
        container.appendChild(cesiumHomeButton);
      }

      const p1 = document.createElement("p");
      const p2 = document.createElement("p");
      p1.classList.add("ditap-function-btn-img");
      p2.classList.add("ditap-btn-tooltip");
      p2.textContent = "처음화면";

      cesiumHomeButton.appendChild(p1);
      cesiumHomeButton.appendChild(p2);

      this._homeButton = cesiumHomeButton;

      const resizeOriginal = () => {
        // 윈도우의 현재 너비를 확인
        const currentIsMobile = Utils.isBodyWidthLessThan600();

        if (this.isMobile !== currentIsMobile) {
          if (currentIsMobile) {
            this.isMobile = true;
            li.appendChild(cesiumHomeButton);
            // mobileContainer.prepend(li);
            mobileContainer.querySelector(".js-position-home").appendChild(li);
            cesiumHomeButton.classList.add("ditap-mobile-btn");
            cesiumHomeButton.classList.remove("ditap-toolbar-btn");
          } else {
            this.isMobile = false;
            cesiumHomeButton.style.display = "flex";
            cesiumHomeButton.classList.remove("ditap-mobile-btn");
            cesiumHomeButton.classList.add("ditap-toolbar-btn");
            container.appendChild(cesiumHomeButton);
          }
        }
      };
      this._resizeEvent = () => {
        resizeOriginal();
        setTimeout(resizeOriginal, 500);
      };

      window.addEventListener("resize", this._resizeEvent);
    }
  }

  /**
   * 위젯 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the widget.  Should be called if permanently removing the widget from layout.
   */
  destroy() {
    // 컨테이너에 요소 있는지 없는지
    if (this._container.getElementsByClassName("ditap-home-btn").length < 1) {
      this._container.appendChild(this._homeButton);
    }
    super.destroy();

    if (this._resizeEvent) {
      window.removeEventListener("resize", this._resizeEvent);
    }
    this._resizeEvent = undefined;
    this._homeButton = undefined;

    this._container = undefined;
    this._viewModel = undefined;
    this._element = undefined;
    this.isDestroyed = undefined;
    this.isMobile = undefined;
  }

  /**
   * 위젯 파괴 여부 반환.</br>
   * Returns the presence or absence of widget destroy.
   * @return {boolean} 객체가 파괴 된 경우 true, 그렇지 않으면 false 반환.</br> True if the object has been destroyed, false otherwise.
   */
  isDestroyed() {
    return super.isDestroyed();
  }
}

export default DitapHomeButton;
